/**
 * Citirea codurilor QR de pe etichete cu camera telefonului.
 *
 * Foloseste detectorul nativ al browserului (BarcodeDetector - Chrome pe
 * Android si macOS) cand stie QR, altfel jsQR: Safari pe iPhone nu are
 * detector nativ. jsQR se incarca separat, doar la prima pornire a camerei pe
 * un astfel de browser.
 *
 * Un cod se raporteaza o singura data cat sta in cadru: acelasi cod se citeste
 * din nou doar dupa ce a lipsit din cadru (eticheta a fost luata din fata
 * camerei si adusa inapoi).
 */

const SCAN_EVERY_MS = 150;

const GONE_AFTER_MS = 700;

// jsQR decodeaza pixelii in JS: o imagine micsorata ajunge pentru o eticheta
// tinuta aproape si tine bucla rapida pe telefoanele mai vechi.
const FALLBACK_MAX_SIDE = 640;

const CONSTRAINTS: MediaStreamConstraints = {
    audio: false,
    video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
    },
};

type Detect = (video: HTMLVideoElement) => Promise<string | undefined>;

type JsQR = typeof import('jsqr').default;

function loadJsQR(): Promise<JsQR> {
    return new Promise((resolve, reject) => {
        require.ensure(
            [],
            () => resolve(require('jsqr').default),
            reject,
            'jsqr',
        );
    });
}

async function nativeDetector(): Promise<Detect | undefined> {
    const Native = (window as any).BarcodeDetector;
    if (!Native) return undefined;

    const formats: string[] = await Native.getSupportedFormats().catch(
        () => [],
    );
    if (formats.indexOf('qr_code') === -1) return undefined;

    const detector = new Native({ formats: ['qr_code'] });

    return async video => {
        const codes = await detector.detect(video);
        return codes.length ? codes[0].rawValue : undefined;
    };
}

async function jsQRDetector(): Promise<Detect> {
    const jsQR = await loadJsQR();
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', {
        willReadFrequently: true,
    } as any) as CanvasRenderingContext2D;

    return async video => {
        const scale = Math.min(
            1,
            FALLBACK_MAX_SIDE / Math.max(video.videoWidth, video.videoHeight),
        );
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const image = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(image.data, image.width, image.height, {
            inversionAttempts: 'dontInvert',
        });

        return code ? code.data : undefined;
    };
}

function cameraError(error: any): string {
    switch (error && error.name) {
        case 'NotAllowedError':
            return 'Accesul la camera este refuzat. Permite-l din setarile browserului pentru acest site.';
        case 'NotFoundError':
        case 'OverconstrainedError':
            return 'Nu am gasit nicio camera pe acest dispozitiv.';
        case 'NotReadableError':
            return 'Camera este folosita de alta aplicatie.';
        default:
            return 'Camera nu a putut porni.';
    }
}

export class QrCamera {
    private stream?: MediaStream;

    private detect?: Detect;

    private timer?: number;

    private seen = { code: '', at: 0 };

    // Fals dupa `stop()`: pornirea poate dura (cererea de permisiune), iar
    // pagina poate fi parasita intre timp.
    private wanted = false;

    constructor(
        private video: HTMLVideoElement,
        private onDetect: (code: string) => void,
    ) {
        // iOS reda video inline doar fara sunet si cu `playsinline`; Vue 2
        // seteaza `muted` ca atribut, nu ca proprietate.
        video.muted = true;
        video.setAttribute('playsinline', '');
    }

    /** Arunca o eroare cu mesajul de afisat daca camera nu porneste. */
    async start(): Promise<void> {
        if (this.wanted) return;

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error(
                'Camera merge doar pe o conexiune securizata (HTTPS).',
            );
        }

        this.wanted = true;

        try {
            const stream = await navigator.mediaDevices.getUserMedia(
                CONSTRAINTS,
            );
            if (!this.wanted) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }

            this.stream = stream;
            this.detect =
                this.detect ||
                (await nativeDetector()) ||
                (await jsQRDetector());
            if (!this.wanted) return;

            this.video.srcObject = stream;
            await this.video.play();
        } catch (error) {
            this.stop();
            throw new Error(cameraError(error));
        }

        this.scanFrame();
    }

    stop() {
        this.wanted = false;
        window.clearTimeout(this.timer);

        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
        }

        this.stream = undefined;
        // tslint:disable-next-line:no-null-keyword — tipul DOM nu accepta undefined
        this.video.srcObject = null;
    }

    get torchAvailable(): boolean {
        const track = this.track();
        const capabilities: any =
            track && track.getCapabilities ? track.getCapabilities() : {};

        return !!capabilities.torch;
    }

    async setTorch(on: boolean) {
        const track = this.track();
        if (track) {
            await track.applyConstraints({ advanced: [{ torch: on } as any] });
        }
    }

    private track(): MediaStreamTrack | undefined {
        return this.stream ? this.stream.getVideoTracks()[0] : undefined;
    }

    private async scanFrame() {
        if (!this.stream || !this.detect) return;

        if (this.video.readyState >= 2) {
            try {
                const code = await this.detect(this.video);
                const now = Date.now();

                if (
                    code &&
                    (code !== this.seen.code ||
                        now - this.seen.at > GONE_AFTER_MS)
                ) {
                    this.onDetect(code.trim());
                }
                if (code) {
                    this.seen = { code, at: now };
                }
            } catch (e) {
                // Un cadru care nu se poate citi (video inca negru) nu
                // opreste camera.
            }
        }

        if (this.stream) {
            this.timer = window.setTimeout(
                () => this.scanFrame(),
                SCAN_EVERY_MS,
            );
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--2!./src/utils/qr-camera.ts